import React from 'react';
import {
  Container,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
  Chip
} from '@mui/material';
import {
  Gavel as LegalIcon,
  LocalShipping as ShippingIcon,
  CreditCard as PaymentIcon,
  Assignment as ContractIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import Link from '@mui/material/Link';

const Terms = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography
          variant="h3"
          sx={{
            mb: 2,
            fontFamily: '"Playfair Display", serif',
            fontWeight: 600,
            color: 'secondary.main'
          }}
        >
          <LegalIcon sx={{ mr: 2, fontSize: 40 }} />
          Términos y Condiciones
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Vinogenes — Tienda online de vinos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Última actualización: Mayo 2026
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sitio web: tienda.vinogenes.es
        </Typography>
      </Box>

      <Alert severity="warning" sx={{ mb: 4 }}>
        <Typography variant="body2">
          Al comprar en Vinogenes aceptas estos términos. La venta de alcohol está limitada a
          mayores de 18 años.
        </Typography>
      </Alert>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom color="secondary.main" sx={{ fontWeight: 600 }}>
          1. Objeto
        </Typography>
        <Typography variant="body1" paragraph>
          Estos términos regulan la compra de vinos a través de la tienda online de Vinogenes
          (tienda.vinogenes.es). El uso del sitio implica la aceptación de estas condiciones y de
          nuestra{' '}
          <Link component={RouterLink} to="/privacidad" color="secondary">
            Política de Privacidad
          </Link>
          .
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom color="secondary.main" sx={{ fontWeight: 600 }}>
          2. Datos del comprador
        </Typography>
        <Typography variant="body1" paragraph>
          Al formalizar un pedido deberás facilitar datos veraces (nombre, email, teléfono y
          dirección de envío). Esos datos se tratarán <strong>únicamente</strong> para gestionar tu
          pedido y realizar el envío, conforme a la Política de Privacidad. No utilizamos tus datos
          para fines comerciales ni publicitarios.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom color="secondary.main" sx={{ fontWeight: 600 }}>
          3. Productos y edad mínima
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Solo mayores de 18 años.</strong> Al comprar declaras ser mayor de edad. Podemos
            solicitar identificación en la entrega.
          </Typography>
        </Alert>
        <List>
          <ListItem>
            <ListItemText primary="Los precios incluyen IVA y se muestran en euros (€)" />
          </ListItem>
          <ListItem>
            <ListItemText primary="La disponibilidad depende del stock existente" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Las imágenes y descripciones son orientativas" />
          </ListItem>
        </List>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom color="secondary.main" sx={{ fontWeight: 600 }}>
          <ContractIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          4. Proceso de compra
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="Añade productos al carrito y completa el formulario de envío" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Paga con tarjeta de débito o crédito mediante Stripe" />
          </ListItem>
          <ListItem>
            <ListItemText primary="El pedido se registra solo tras confirmarse el pago" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Recibirás un email de confirmación con el resumen del pedido" />
          </ListItem>
        </List>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom color="secondary.main" sx={{ fontWeight: 600 }}>
          <PaymentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          5. Precios y pago
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="Los gastos de envío se calculan según zona y peso del pedido" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Pueden aplicarse códigos de descuento según sus condiciones" />
          </ListItem>
        </List>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 2 }}>
          <Chip label="Tarjeta de crédito" color="primary" variant="outlined" />
          <Chip label="Tarjeta de débito" color="primary" variant="outlined" />
          <Chip label="Pago seguro con Stripe" color="success" variant="outlined" />
        </Box>
        <Typography variant="body2" color="text.secondary">
          El cobro lo procesa Stripe. Vinogenes no almacena los datos completos de tu tarjeta.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom color="secondary.main" sx={{ fontWeight: 600 }}>
          <ShippingIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          6. Envíos
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="España peninsular" secondary="Plazo estimado: 2-3 días laborables" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Islas Baleares" secondary="Plazo estimado: 3-5 días laborables" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Islas Canarias" secondary="Plazo estimado: 5-7 días laborables" />
          </ListItem>
        </List>
        <Typography variant="body1" paragraph sx={{ mt: 2 }}>
          Es necesario que haya una persona mayor de edad en la dirección de entrega. Los plazos son
          orientativos y pueden variar por causas logísticas ajenas a Vinogenes.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom color="secondary.main" sx={{ fontWeight: 600 }}>
          7. Devoluciones
        </Typography>
        <Typography variant="body1" paragraph>
          Puedes ejercer el derecho de desistimiento en un plazo de 14 días naturales desde la
          recepción, siempre que el producto no haya sido abierto y conserve su embalaje original.
          Los gastos de devolución corren por cuenta del cliente salvo producto defectuoso o error
          nuestro. El reembolso se efectuará por el mismo medio de pago una vez recibida y revisada
          la devolución.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom color="secondary.main" sx={{ fontWeight: 600 }}>
          8. Responsabilidad
        </Typography>
        <Typography variant="body1" paragraph>
          Vinogenes trabaja con productos en buen estado y transportistas de confianza. No nos
          hacemos responsables del mal uso del alcohol ni del consumo por menores de edad.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom color="secondary.main" sx={{ fontWeight: 600 }}>
          9. Legislación y contacto
        </Typography>
        <Typography variant="body1" paragraph>
          Estas condiciones se rigen por la legislación española. Para consultas:
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="Email: info@vinogenes.es" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Teléfono: +34 653 56 29 30" />
          </ListItem>
        </List>
      </Box>

      <Alert severity="success" sx={{ mt: 4 }}>
        <Typography variant="body2">
          <strong>Vinogenes</strong> — Transparencia en la compra y respeto a tus datos. Solo los
          usamos para enviar tu pedido.
        </Typography>
      </Alert>

      <Box sx={{ textAlign: 'center', mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Vinogenes — Términos y Condiciones vigentes desde Mayo 2026
        </Typography>
      </Box>
    </Container>
  );
};

export default Terms;
